import { PERMISSIONS } from "@/modules/auth/useSession";
import { canAccessManagementPath, holdsAdministrationPermission, holdsBackOfficePermission } from "@/modules/auth/backOffice";
import type { NavIconKey } from "./navIcons";
import type { ManageNavGroup } from "./nav";

/**
 * The back office's destinations, as DATA — the single source of truth for
 * both the nav that renders them and the redirect that picks one.
 *
 * It exists because there used to be two lists. `ManageShell` built the real
 * one inside its own body (it needs `useTranslate` for the labels), and
 * `ManageIndexRedirect` carried a hardcoded copy of five of the thirteen rows
 * plus a `?? "invoices"` fallback. Every row the copy did not know about was a
 * role stranded on arrival: a REPORT_VIEW-only, SHIFT_MANAGE-only,
 * TIER_MANAGE-only or MEMBER/CATEGORY-only session landed on the invoices
 * permission wall while its own row sat in the sidebar beside it.
 *
 * So the rows are declared LABEL-FREE here — path, permissions, glyph and
 * group — and the shell adds the labels with the translator it already has.
 * A row added below appears in the nav AND becomes a legal landing in the same
 * commit; neither list can drift from the other because there is only one.
 */

/**
 * A row's key. It is also its `nav.*` message key, which is what lets the
 * shell label the list without a second lookup table to keep in sync.
 */
export type ManageRowKey =
  // UX-round D62: there is no `pos` key any more. `manage/tables` («الطاولات», service)
  // «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
  // and `manage/floor` («الصالة والطاولات», store) were two rows onto the same
  // areas and the same tables; they are one row, `floor`, with two views.
  | "today"
  | "menu"
  | "floor"
  | "addons"
  | "categories"
  | "ingredients"
  | "receiveDelivery"
  | "stocktake"
  | "suppliers"
  | "aggregators"
  | "invoices"
  | "customers"
  | "shift"
  | "kitchen"
  | "settings"
  | "staff"
  | "reports"
  | "reportItems"
  | "reportStaff"
  | "reportDayparts"
  | "reportRemakes"
  | "reportSpeed"
  | "reportRefunds"
  | "feedback"
  | "reportInputVat"
  | "shiftHistory"
  | "dayClose";

/**
 * A store toggle that can hide a row.
 *
 * Both fail OPEN when unknown — see the notes at the shell's call site: a row
 * hidden on a flaky settings fetch makes the nav appear to lose entries at
 * random.
 */
export type ManageRowFeature = "kitchenDisplay" | "shiftTracking";

export type ManageRowConfig = {
  key: ManageRowKey;
  /** The route under `/:locale`, with no leading slash. */
  path: string;
  icon: NavIconKey;
  group: ManageNavGroup;
  /** ANY of these is enough to see the row. */
  permissions: readonly string[];
  /** The store toggle this row hides with, if any. */
  feature?: ManageRowFeature;
  /**
   * Whether the row is a SECTION OF the back office, as opposed to a link out
   * of it.
   *
   * Two rows are links out — the till and the kitchen board — and they are the
   * reason bare `/manage` cannot simply land on the first visible row. The
   * till's own exit-X points AT `/manage`; a redirect that sent a POS_USE
   * holder straight back to `/pos` would make that control a no-op that
   * bounces. So the landing considers only the rows that live here.
   */
  isSection: boolean;
};

/**
 * Order matters twice: the sidebar groups these but preserves their relative
 * order inside a group, and the bare-`/manage` landing takes the FIRST visible
 * section. Service concerns first, as on the floor.
 *
 * SIX groups, by JOB, since the 2026-10-14 round table (D152, T8): service
 * (الصالة) · money (المالية) · menu · inventory (المشتريات والمخزون) · reports · store. The
 * October redesign's five grouped by screen type, which split the cash job in
 * two (the live drawer under service, its log and the تقفيلة under reports)
 * and left ten rows under reports. The money group is the floor manager's
 * whole close, top to bottom (`MONEY_ORDER`). Moving a row between groups
 * changes neither its gate nor the landing.
 */
export const MANAGE_ROWS: readonly ManageRowConfig[] = [
  /*
   * TODAY — the back office's home (D111, founder 2026-10-02; supersedes D87).
   *
   * First in the list, so it heads the service group and is the first section
   * `manageLandingPath` finds. Its blocks each read the endpoint of the page
   * they open (`modules/manage/lib/today.ts`), and the row is gated on the
   * three grants under which a MANAGEMENT block renders:
   *
   * - `REPORT_OPERATIONS_VIEW` → gross sales today (the Z) — the MANAGER;
   * - `REPORT_VIEW` → the same, plus top sellers today — the OWNER;
   * - `COSTING_MANAGE` → ingredients to reorder and the needs-attention list.
   *
   * **`MENU_VIEW` is deliberately NOT here,** although it opens a block (the
   * items-available tile). Every floor role holds it, so adding it would land
   * a waiter or a cashier on a home whose only content is a count the menu —
   * their landing until now — already shows. A session holding none of the
   * three keeps its old landing; one that types the URL still gets the tile.
   * `rows.spec.ts` checks every grant listed opens at least one block.
   */
  { key: "today", path: "manage/today", icon: "today", group: "service", permissions: [PERMISSIONS.REPORT_OPERATIONS_VIEW, PERMISSIONS.REPORT_VIEW, PERMISSIONS.COSTING_MANAGE], isSection: true },
  /*
   * THE FLOOR — one row, two permissions, two views (UX-round D62).
   *
   * It was two rows: `manage/tables` on `POS_USE` in the service group, and
   * `manage/floor` on `FLOOR_MANAGE` in the store group, showing the same areas
   * and the same tables with the same «إضافة طاولة» button on both. One row
   * now, first in service order, so the whole floor still lands here.
   *
   * **ANY-of is doing real work in this row, and it is the risk G2 named.** A
   * `POS_USE` session sees the row and reaches the LIVE view only; the layout
   * view — every create, rename, reorder, pause and delete — re-checks
   * `FLOOR_MANAGE` inside `FloorPage`. This list is orientation, not the gate:
   * widening it here must never be read as widening what the screen offers.
   */
  { key: "floor", path: "manage/floor", icon: "pos", group: "service", permissions: [PERMISSIONS.POS_USE, PERMISSIONS.FLOOR_MANAGE], isSection: true },
  /*
   * MENU_MANAGE **or** MENU_VIEW — and NOT `MENU_AVAILABILITY` (B117/E1).
   *
   * The availability arm was added for D32's reason, and that reason was the
   * CASHIER: the 86 switch lives on this screen and the cashier preset holds
   * availability without manage, so a manage-only gate would have hidden the
   * till's own control behind a nav item they could not see.
   *
   * **D50 then put `MENU_AVAILABILITY` on KITCHEN too**, and the arm quietly
   * started meaning something it was never written to mean. A cook got the
   * «المنيو» row, was LANDED there by bare `/ar/manage` (see
   * `manageLandingPath`), and read a page headed «الأصناف اللي بتتباع،
   * وتكلفتها» — "the items that are sold, AND THEIR COST" — saying «اضغط على
   * صنف… وتعدّله هنا». Nothing leaked: `GET /restaurant/menu-items` 403s twice
   * on `RESTAURANT_MENU_VIEW` underneath. The screen simply never said so.
   * And the cook's own 86 screen is `/kitchen/availability`, not this one, so
   * the arm was not even buying them the control it exists for.
   *
   * `MENU_VIEW` is the permission this page's own endpoint requires, so gating
   * on it is the gate agreeing with the server instead of guessing. The CASHIER
   * holds it (system catalog) and keeps the row; KITCHEN does not and loses it.
   * `MENU_MANAGE` stays beside it so a custom role minted with manage alone is
   * not locked out of a screen it is fully entitled to.
   */
  { key: "menu", path: "manage/menu", icon: "menu", group: "menu", permissions: [PERMISSIONS.MENU_MANAGE, PERMISSIONS.MENU_VIEW], isSection: true },
  { key: "addons", path: "manage/addons", icon: "addons", group: "menu", permissions: [PERMISSIONS.MENU_MANAGE], isSection: true },
  // `CATEGORY_VIEW`, not a `RESTAURANT_*` permission: the categories screen
  // drives the seller category routes. OWNER and MANAGER hold it; WAITER and
  // CASHIER do not.
  { key: "categories", path: "manage/categories", icon: "categories", group: "menu", permissions: [PERMISSIONS.CATEGORY_VIEW], isSection: true },
  { key: "ingredients", path: "manage/ingredients", icon: "ingredients", group: "inventory", permissions: [PERMISSIONS.COSTING_MANAGE], isSection: true },
  /*
   * Receiving a delivery — استلام بضاعة (D147). Right after Stock, on its OWN
   * grant (`DELIVERY_RECORD`, the one every `/restaurant/deliveries` route
   * requires) and deliberately NOT on `COSTING_MANAGE`: the CASHIER and the
   * MANAGER presets hold it, and this row is the first inventory door they
   * can see — a door the cashier cannot see is a door nobody uses
   * (the storekeeper's condition on the sidebar regroup, T8). Its own path,
   * not `manage/ingredients/…`, so Stock keeps its prefix match.
   */
  { key: "receiveDelivery", path: "manage/deliveries/new", icon: "receiveDelivery", group: "inventory", permissions: [PERMISSIONS.DELIVERY_RECORD], isSection: true },
  // The count screen (D143, phase 9) — beside the stock it counts, on the same
  // `COSTING_MANAGE` grant every `/restaurant/stocktakes` route requires. Its
  // own path (not `manage/ingredients/…`) so "Stock" keeps prefix matching for
  // the item card beneath it and this row lights up on its own.
  { key: "stocktake", path: "manage/stocktake", icon: "stocktake", group: "inventory", permissions: [PERMISSIONS.COSTING_MANAGE], isSection: true },
  // Suppliers (B45, unit 5) — reference data for the purchase form, on the
  // same `COSTING_MANAGE` grant as the ingredients it is entered beside. NOT
  // `REPORT_VIEW`: recording who a delivery came from is the buyer's job, and
  // reading the store's tax position off it is the owner's.
  { key: "suppliers", path: "manage/suppliers", icon: "suppliers", group: "inventory", permissions: [PERMISSIONS.COSTING_MANAGE], isSection: true },
  // Gated on `POS_USE`, NOT on a `*_MANAGE`. `GET /restaurant/invoices` is
  // decorated `@RequirePermission(RESTAURANT_POS_USE)`, and
  // `RESTAURANT_SYSTEM_ROLE_CATALOG` gives that permission to WAITER and
  // CASHIER as well as OWNER — so this is the one back-office row the whole
  // floor is entitled to, and copying an owner-only gate from a sibling row
  // would hide it from them.
  { key: "invoices", path: "manage/invoices", icon: "invoices", group: "service", permissions: [PERMISSIONS.POS_USE], isSection: true },
  // The customer list (founder decision 2026-10-25) — everyone with a phone,
  // from the website or typed at the till. Right after the bills it is read
  // beside, on its OWN grant (`CUSTOMER_VIEW`, every `/restaurant/customers`
  // route's): the OWNER, MANAGER and CASHIER presets hold it, the waiter and
  // the cook do not. Blocking is a second grant, checked inside the drawer.
  { key: "customers", path: "manage/customers", icon: "customers", group: "service", permissions: [PERMISSIONS.CUSTOMER_VIEW], isSection: true },
  // The cash-drawer shift feature (D23-D29). Hides while `shiftTrackingEnabled`
  // is off: a screen behind a toggle nobody flipped is not a destination.
  { key: "shift", path: "manage/shift", icon: "shift", group: "money", permissions: [PERMISSIONS.SHIFT_MANAGE], feature: "shiftTracking", isSection: true },
  // The kitchen board. It lives at `/kitchen`, OUTSIDE the back-office shell —
  // see the note on the route — but it is still linked from here, because the
  // owner and the cashier who hold `RESTAURANT_KITCHEN_VIEW` reach it from the
  // back office and would otherwise need the URL. A cook holding only the
  // KITCHEN role sees no SECTION here at all and lands on the board directly;
  // that is the intended shape, not a gap.
  { key: "kitchen", path: "kitchen", icon: "kitchen", group: "service", permissions: [PERMISSIONS.KITCHEN_VIEW], feature: "kitchenDisplay", isSection: false },
  // ONE row for the whole settings area, gated on EITHER of the two
  // permissions the hub fans out to: `SETTINGS_MANAGE` for the general form,
  // `TIER_MANAGE` for price tiers. The system catalog gives both only to
  // OWNER, but a custom role can be minted with either one alone, and a
  // single-permission gate would hide price tiers from a tier-only role that
  // is fully entitled to them. The hub filters its own rows per permission, so
  // widening here cannot expose a section the session may not use.
  // Aggregators (F2, unit 4) — who brought the customer, and the rate they
  // take. EDITING is on `SETTINGS_MANAGE` and NOT the costing grant its
  // supplier sibling uses: a supplier is reference data for the purchase form
  // and carries no money of its own, while a commission RATE reaches a bill's
  // stored total. READING the screen is also open to whoever may read the
  // platforms' period totals — the owner's or the floor manager's reports
  // grant (founder P6-Q5, option A, 2026-10-27) — read-only for them.
  { key: "aggregators", path: "manage/aggregators", icon: "suppliers", group: "store", permissions: [PERMISSIONS.SETTINGS_MANAGE, PERMISSIONS.REPORT_VIEW, PERMISSIONS.REPORT_OPERATIONS_VIEW], isSection: true },
  { key: "settings", path: "manage/settings", icon: "settings", group: "store", permissions: [PERMISSIONS.SETTINGS_MANAGE, PERMISSIONS.TIER_MANAGE], isSection: true },
  // Gated on what the screen's own endpoints require. `MEMBER_VIEW` is held by
  // the OWNER and by the shared MANAGER preset (its shop-manager half, kept in
  // a restaurant store) — so the floor manager sees it too (B148 corrected the
  // old "OWNER-only" note, 2026-10-14). Driver backend B5 · P18: also on the
  // two driver-roster grants — a cashier's Team is its Drivers tab (the page
  // sends a session without MEMBER_VIEW / ROLE_VIEW straight there).
  { key: "staff", path: "manage/staff", icon: "staff", group: "store", permissions: [PERMISSIONS.MEMBER_VIEW, PERMISSIONS.DISPATCH_MANAGE, PERMISSIONS.DRIVER_DOCUMENTS_VIEW], isSection: true },
  // The spent-vs-gained report (phase 2). Its own permission, held by no system
  // role below OWNER — see the controller's header comment.
  { key: "reports", path: "manage/reports", icon: "reports", group: "reports", permissions: [PERMISSIONS.REPORT_VIEW], isSection: true },
  // The tier-2 reports (S1, S3, S4). Same permission and glyph family as
  // `reports` above — reusing the "reports" icon rather than minting three
  // new glyphs for four screens in the same nav group. Each is its own
  // ROUTE and its own row (E16), not a tab on the spend-gain screen.
  { key: "reportItems", path: "manage/reports/items", icon: "reports", group: "reports", permissions: [PERMISSIONS.REPORT_VIEW], isSection: true },
  { key: "reportStaff", path: "manage/reports/staff", icon: "reports", group: "money", permissions: [PERMISSIONS.REPORT_OPERATIONS_VIEW, PERMISSIONS.REPORT_VIEW], isSection: true },
  { key: "reportDayparts", path: "manage/reports/dayparts", icon: "reports", group: "reports", permissions: [PERMISSIONS.REPORT_OPERATIONS_VIEW, PERMISSIONS.REPORT_VIEW], isSection: true },
  // B117/Q5 — send-backs and their waste. On the OPERATIONS rung because the
  // gap it closes is the MANAGER's: remake cost otherwise sits only in reports
  // no preset below OWNER can open, so the person running the service was being
  // measured on a figure they could not read.
  { key: "reportRemakes", path: "manage/reports/remakes", icon: "reports", group: "reports", permissions: [PERMISSIONS.REPORT_OPERATIONS_VIEW, PERMISSIONS.REPORT_VIEW], isSection: true },
  // B124/D114 — where the time goes, from a round going in to the food at the
  // table. Timings only: no money, no cost, no names, so the supervision rung.
  { key: "reportSpeed", path: "manage/reports/speed", icon: "reports", group: "reports", permissions: [PERMISSIONS.REPORT_OPERATIONS_VIEW, PERMISSIONS.REPORT_VIEW], isSection: true },
  // Founder decision 5 (2026-10-02) — one row per refund, with its markers. The
  // same supervision rung as the staff report, which counts these refunds per
  // person: the endpoint takes either grant.
  { key: "reportRefunds", path: "manage/reports/refunds", icon: "reports", group: "money", permissions: [PERMISSIONS.REPORT_OPERATIONS_VIEW, PERMISSIONS.REPORT_VIEW], isSection: true },
  // D134 — what diners said after paying (the table app's feedback). No money,
  // and the person running the service is the one who acts on it, so the
  // supervision rung like service speed: the endpoint takes either grant.
  { key: "feedback", path: "manage/feedback", icon: "reports", group: "reports", permissions: [PERMISSIONS.REPORT_OPERATIONS_VIEW, PERMISSIONS.REPORT_VIEW], isSection: true },
  // The input side of the VAT return (F8, unit 5) — the fourth sibling report,
  // same permission and same glyph family as the three above it.
  { key: "reportInputVat", path: "manage/reports/input-vat", icon: "reports", group: "money", permissions: [PERMISSIONS.REPORT_VIEW], isSection: true },
  // The owner's review of past shifts. `REPORT_VIEW`, not `SHIFT_MANAGE` —
  // variance is margin-grade sensitive, same as `reports` above.
  { key: "shiftHistory", path: "manage/shifts", icon: "shiftHistory", group: "money", permissions: [PERMISSIONS.REPORT_OPERATIONS_VIEW, PERMISSIONS.REPORT_VIEW], feature: "shiftTracking", isSection: true },
  // The Z (D38-D41): a business day, read. `REPORT_VIEW`, same reasoning as
  // `reports` and `shiftHistory` above — takings are margin-grade sensitive.
  // Deliberately NOT gated on `shiftTracking`: block A (the day's sales)
  // stands alone for a store that runs no drawer at all.
  { key: "dayClose", path: "manage/day-close", icon: "invoices", group: "money", permissions: [PERMISSIONS.REPORT_OPERATIONS_VIEW, PERMISSIONS.REPORT_VIEW], isSection: true },
];

/** Which store toggles are on. Unknown fails OPEN — see `ManageRowFeature`. */
export type ManageRowFeatures = Record<ManageRowFeature, boolean>;

/**
 * The rows a session may see.
 *
 * Permission filtering is STRICT: no permissions means no rows. There is
 * deliberately no "empty set means show everything" fallback — a fail-open on
 * a nav that reaches menu pricing and staff roles is the wrong way to fail.
 * Server-side `@RequirePermission` is still the real gate; this is orientation.
 */
export function visibleManageRows(
  permissions: readonly string[] | undefined | null,
  features: ManageRowFeatures,
): ManageRowConfig[] {
  const held = permissions ?? [];
  const administration = holdsAdministrationPermission(held);
  const rows = MANAGE_ROWS.map((row) => {
    if (administration) return row;
    if (row.key === "staff") return { ...row, path: "manage/staff/drivers" };
    if (row.key === "menu" && holdsBackOfficePermission(held)) {
      return { ...row, path: "kitchen/availability", permissions: [PERMISSIONS.MENU_AVAILABILITY], isSection: false };
    }
    return row;
  });
  return rows.filter(
    (row) =>
      (!row.isSection || canAccessManagementPath(held, row.path)) &&
      (row.feature ? features[row.feature] : true) &&
      row.permissions.some((permission) => held.includes(permission)),
  );
}

/**
 * Where bare `/manage` lands — the first back-office SECTION this session can
 * see, in the nav's own order, or `null` when it can see none.
 *
 * `null` is a real answer, not a failure to find one: it is the case that used
 * to be papered over with `?? "invoices"`, which sent a cook (and any custom
 * role without `POS_USE`) to a permission wall. The caller renders an
 * explanation with a way out instead of redirecting.
 */
export function manageLandingPath(
  locale: string,
  permissions: readonly string[] | undefined | null,
  features: ManageRowFeatures,
): string | null {
  const sections = visibleManageRows(permissions, features).filter(
    (row) => row.isSection,
  );

  /*
   * TODAY is the back office's home when the session can reach it, then the
   * MENU, then the first section (D111, founder 2026-10-02).
   *
   * The menu held this place from 2026-09-10 (B34/D87), when the overview the
   * artboards open on was rejected: it "would just make us run heavy queries in
   * the db" and earned nothing new. The founder reversed that with the 2026-10
   * redesign, and the home was built to answer the cost half — it reads only
   * the endpoints of the pages its tiles open, for the current business day,
   * under each block's own permission, with no polling (see `TodayPage`).
   *
   * Resolved by KEY, not by position, so re-ordering the list cannot silently
   * move the front door. `find` over the visible rows, not a hardcoded path, so
   * administrative sessions prefer Today, then the menu. Operational sessions
   * prefer bills, then their drawer; floor-only sessions have no section here. And when nothing is reachable this still returns null rather than
   * redirecting onto a permission wall — see `ManageIndexRedirect`, which
   * explains instead.
   */
  const landing =
    holdsAdministrationPermission(permissions)
      ? sections.find((row) => row.key === "today") ??
        sections.find((row) => row.key === "menu") ??
        sections[0]
      : sections.find((row) => row.key === "invoices") ??
        sections.find((row) => row.key === "shift") ??
        sections[0];
  return landing ? `/${locale}/${landing.path}` : null;
}
